import React from 'react';
import { Button, Dialog, DialogTrigger, DialogContent, DialogFooter, Flex, Heading, LoadingOrbit, Text } from '@chatsdet/ui';
import { ArrowsClockwise } from '@phosphor-icons/react';
import {
  useApplicationContext,
  useBootstrapStatus,
  useUpsertContextItem,
  useDeleteContextItem,
  useTriggerBootstrap,
  useCancelBootstrap,
} from './useApplicationContext';
import { useBrowserStream } from '../../hooks/useBrowserStream';
import { ContextSectionCard } from './components/ContextSectionCard';
import { EditContextItemDialog } from './components/EditContextItemDialog';
import { VersionHistoryDialog } from './components/VersionHistoryDialog';
import { EmptyContextState } from './components/EmptyContextState';
import { BootstrapLiveView } from './components/BootstrapLiveView';
import type { ApplicationContextItem, ApplicationContextSection } from './types';
import { SECTION_ORDER } from './types';

type Props = {
  propertyId: string;
  hasLegacyContext?: boolean;
};

export function PropertyContextTab({ propertyId, hasLegacyContext = false }: Props) {
  const contextQ = useApplicationContext(propertyId);
  const bootstrapQ = useBootstrapStatus(propertyId);
  const upsertMutation = useUpsertContextItem(propertyId);
  const deleteMutation = useDeleteContextItem(propertyId);
  const bootstrapMutation = useTriggerBootstrap(propertyId);
  const cancelMutation = useCancelBootstrap(propertyId);

  // Dialog state
  const [editItem, setEditItem] = React.useState<ApplicationContextItem | null>(null);
  const [addSection, setAddSection] = React.useState<ApplicationContextSection | null>(null);
  const [historyItem, setHistoryItem] = React.useState<ApplicationContextItem | null>(null);
  const [deleteItem, setDeleteItem] = React.useState<ApplicationContextItem | null>(null);

  // Track stream ID from bootstrap trigger (immediate) or from polling (reconnect)
  const [activeStreamId, setActiveStreamId] = React.useState<string | null>(null);

  const isDialogOpen = editItem !== null || addSection !== null;
  const isHistoryOpen = historyItem !== null;
  const isBootstrapping =
    bootstrapQ.data?.bootstrap?.status === 'running' ||
    bootstrapQ.data?.bootstrap?.status === 'pending';

  // Clear stream ID when bootstrap finishes
  React.useEffect(() => {
    if (!isBootstrapping && activeStreamId) {
      setActiveStreamId(null);
    }
  }, [isBootstrapping, activeStreamId]);

  // Use stream ID from trigger response, or fall back to bootstrap record's jobId on reconnect
  const streamId = activeStreamId ?? (isBootstrapping ? (bootstrapQ.data?.bootstrap?.jobId ?? null) : null);
  const browserStream = useBrowserStream(streamId);

  const handleSave = (data: {
    section: ApplicationContextSection;
    itemKey: string;
    itemLabel: string;
    content: string;
  }) => {
    upsertMutation.mutate(data, {
      onSuccess: () => {
        setEditItem(null);
        setAddSection(null);
      },
    });
  };

  const handleDeleteConfirm = () => {
    if (!deleteItem) return;
    deleteMutation.mutate(
      { section: deleteItem.section, itemKey: deleteItem.itemKey },
      { onSuccess: () => setDeleteItem(null) }
    );
  };

  const handleRestore = (item: ApplicationContextItem) => {
    upsertMutation.mutate(
      {
        section: item.section,
        itemKey: item.itemKey,
        itemLabel: item.itemLabel,
        content: item.content,
      },
      {
        onSuccess: () => setHistoryItem(null),
      }
    );
  };

  const handleBootstrap = () => {
    bootstrapMutation.mutate({}, {
      onSuccess: (data) => {
        setActiveStreamId(data.streamId);
      },
    });
  };

  if (contextQ.isLoading || bootstrapQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingOrbit size="lg" />
      </div>
    );
  }

  const items = contextQ.data?.items ?? [];
  const grouped = contextQ.data?.grouped ?? {};

  const handleCancel = () => {
    cancelMutation.mutate(undefined, {
      onSuccess: () => setActiveStreamId(null),
    });
  };

  // Bootstrap running — show split layout: 2/3 report, 1/3 browser stream
  if (isBootstrapping) {
    return (
      <BootstrapLiveView
        browserStream={browserStream}
        findings={browserStream.state.contextFindings}
        itemsFromDb={items}
        onCancel={handleCancel}
        isCancelling={cancelMutation.isPending}
      />
    );
  }

  // Empty state
  if (items.length === 0) {
    return (
      <>
        <EmptyContextState
          onBootstrap={handleBootstrap}
          onAddManually={() => setAddSection('general')}
          hasLegacyContext={hasLegacyContext}
          isBootstrapping={bootstrapMutation.isPending}
        />
        <EditContextItemDialog
          isOpen={isDialogOpen}
          onClose={() => {
            setEditItem(null);
            setAddSection(null);
          }}
          onSave={handleSave}
          item={editItem}
          section={addSection ?? undefined}
          isSaving={upsertMutation.isPending}
        />
      </>
    );
  }

  return (
    <div className="space-y-4">
      <Flex align="center" justify="between">
        <div>
          <Heading level={3} className="text-foreground">
            App Skill
          </Heading>
          <Text size="sm" className="text-muted-foreground">
            {items.length} {items.length === 1 ? 'item' : 'items'} across{' '}
            {Object.keys(grouped).length} sections
          </Text>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onPress={handleBootstrap}
          isDisabled={bootstrapMutation.isPending}
        >
          <ArrowsClockwise size={14} />
          Re-run Bootstrap
        </Button>
      </Flex>

      <div className="space-y-3">
        {SECTION_ORDER.map((section) => (
          <ContextSectionCard
            key={section}
            section={section}
            items={(grouped[section] as ApplicationContextItem[]) ?? []}
            onEditItem={setEditItem}
            onDeleteItem={setDeleteItem}
            onViewHistory={setHistoryItem}
            onAddItem={setAddSection}
          />
        ))}
      </div>

      <EditContextItemDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setEditItem(null);
          setAddSection(null);
        }}
        onSave={handleSave}
        item={editItem}
        section={addSection ?? undefined}
        isSaving={upsertMutation.isPending}
      />

      <VersionHistoryDialog
        isOpen={isHistoryOpen}
        onClose={() => setHistoryItem(null)}
        propertyId={propertyId}
        section={historyItem?.section ?? null}
        itemKey={historyItem?.itemKey ?? null}
        itemLabel={historyItem?.itemLabel ?? null}
        onRestore={handleRestore}
      />

      <DialogTrigger isOpen={deleteItem !== null} onOpenChange={(open) => { if (!open) setDeleteItem(null); }}>
        <Button className="hidden">Trigger</Button>
        <Dialog
          title="Delete Context Item"
          description={`Delete "${deleteItem?.itemLabel}" and all its version history?`}
        >
          <DialogContent>
            <Text size="sm" className="text-muted-foreground">
              This will permanently remove this item and all its versions. This cannot be undone.
            </Text>
          </DialogContent>
          <DialogFooter>
            <Button variant="outline" onPress={() => setDeleteItem(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onPress={handleDeleteConfirm}
              isDisabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </div>
  );
}
